import type { ReactNode } from 'react'

import type {
  ModalBlockAlign,
  ModalBlockPayload,
  ModalBlockSpacing,
  ModalCardStyle,
  ModalCtaStyle,
  ModalHeadingSize,
  ModalMediaAlign,
  ModalMediaWidth,
  ModalParagraphStyle,
  ModalRowLayout,
} from './modal-blocks/modal-block-types'
import { isSafeAnnouncementLink, isStagedMediaUrl } from './safe-link'
import { modalCtaStore } from './store-link'

export type DroppedModalBlock = {
  path: string
  reason: string
  type: string
}

/**
 * A field a block carried that this build does not render, so the renderer used
 * its default instead.
 *
 * Deliberately not a `DroppedModalBlock`: that means "this block renders
 * nothing", which `hasRenderableModalBlocks` reads to skip a whole candidate. A
 * downgraded field still renders, so folding the two together would make the
 * host's "block dropped" reports false.
 */
export type DowngradedModalField = {
  path: string
  field: 'align' | 'size' | 'spacing' | 'style' | 'width' | keyof ModalRowLayout
  type: string
  value: string
}

/** Carried by every block that can open a line of the card; a row column ignores it. */
type Spaced = { spacing?: ModalBlockSpacing }

type RenderableLeafModalBlock =
  | (Spaced & {
      type: 'heading'
      align?: ModalBlockAlign
      highlight?: string
      size?: ModalHeadingSize
      text: ReactNode
    })
  | (Spaced & {
      type: 'paragraph'
      align?: ModalBlockAlign
      highlight?: string
      style?: ModalParagraphStyle
      text: ReactNode
    })
  | (Spaced & {
      type: 'bullets'
      align?: ModalBlockAlign
      items: readonly ReactNode[]
    })
  | (Spaced & { type: 'image'; url: string; align?: ModalMediaAlign; width?: ModalMediaWidth })
  | (Spaced & { type: 'video'; url: string; align?: ModalMediaAlign; width?: ModalMediaWidth })
  // An absent `link` is a button with no destination: the renderer draws the
  // control and gives it nothing to navigate to. Only `allowLinklessCta` can
  // produce one, so a customer surface never sees this shape.
  | (Spaced & { type: 'cta'; link?: string; style: ModalCtaStyle; text: ReactNode })
  | (Spaced & { type: 'card'; heading: ReactNode; style?: ModalCardStyle; text: ReactNode })

type RenderableRowChildModalBlock = RenderableLeafModalBlock | { type: 'empty' }

export type RenderableModalBlock =
  | RenderableRowChildModalBlock
  | (Spaced & { type: 'hero'; url: string })
  | (Spaced & ModalRowLayout & { type: 'row'; children: readonly RenderableRowChildModalBlock[] })

/**
 * `allowStagedMedia` widens the media branches to a browser object URL, so the
 * admin preview can draw a file that has not reached the bucket. Customer
 * surfaces leave it unset: a stored block can only ever name an http(s) or
 * in-app asset, and the backend applies that same rule on write.
 *
 * `allowLinklessCta` keeps a button whose label is set and whose link is still
 * BLANK — absent, not a string, or nothing but whitespace — and renders it with
 * no destination at all. A design import carries labels and never hrefs, so an
 * imported draft sits in exactly that state until the operator fills the link in
 * — and an operator who cannot see the button cannot judge the design they just
 * imported. The form reports the missing link on its own; hiding the control
 * repeats that report as a disappearance.
 *
 * It widens NOTHING else. A link that is present but malformed still drops the
 * block, on both surfaces, because that is what a customer gets. Customer
 * surfaces leave the option unset and are unchanged.
 */
export type ModalBlockRenderOptions = { allowLinklessCta?: boolean; allowStagedMedia?: boolean }

/**
 * Whether the media source may reach a `src` attribute.
 *
 * Separate from the CTA branch's check on purpose. Every value that branch lets
 * through still passes `isSafeAnnouncementLink` itself, so no option can ever
 * widen an `href`: `allowLinklessCta` only admits a BLANK link, and the block it
 * produces carries no `href` at all.
 */
const isRenderableMediaUrl = (
  url: string,
  { allowStagedMedia }: ModalBlockRenderOptions,
): boolean => isSafeAnnouncementLink(url) || (allowStagedMedia === true && isStagedMediaUrl(url))

// A Record keyed by the union is what makes each set COMPLETE. A
// `readonly ModalMediaAlign[]` annotation only asserts that every member listed
// is valid, so a member the backend adds later can be left out of the set: the
// union widens, the class Record forces a class, and this set forces nothing.
// Every block carrying the new value then downgrades to the fallback with no
// type error, no lint error and no runtime signal.
const MODAL_BLOCK_ALIGNS = {
  left: true,
  center: true,
} as const satisfies Record<ModalBlockAlign, true>

const MODAL_MEDIA_ALIGNS = {
  left: true,
  center: true,
  right: true,
} as const satisfies Record<ModalMediaAlign, true>

const MODAL_MEDIA_WIDTHS = {
  full: true,
  large: true,
  medium: true,
  small: true,
} as const satisfies Record<ModalMediaWidth, true>

const MODAL_CTA_STYLES = {
  primary: true,
  secondary: true,
  inline_link: true,
} as const satisfies Record<ModalCtaStyle, true>

const MODAL_BLOCK_SPACINGS = {
  default: true,
  small: true,
  none: true,
} as const satisfies Record<ModalBlockSpacing, true>

const MODAL_HEADING_SIZES = {
  default: true,
  large: true,
} as const satisfies Record<ModalHeadingSize, true>

const MODAL_PARAGRAPH_STYLES = {
  body: true,
  emphasis: true,
  lead: true,
} as const satisfies Record<ModalParagraphStyle, true>

const MODAL_CARD_STYLES = {
  default: true,
  accent: true,
} as const satisfies Record<ModalCardStyle, true>

const MODAL_ROW_CHOICES = {
  layout: { equal: true, compact: true },
  gap: { small: true, medium: true, large: true },
  vertical_align: { top: true, center: true, bottom: true },
  horizontal_align: { left: true, center: true, right: true },
  mobile_layout: { stack: true, inline: true },
  mobile_align: { inherit: true, center: true },
} as const satisfies {
  [Field in keyof Required<ModalRowLayout>]: Record<NonNullable<ModalRowLayout[Field]>, true>
}

/**
 * One `align` field carries two different value sets, because the wire gives the
 * text blocks and the media blocks the same property name for a different set of
 * choices. Parsing each branch against its own set is what keeps
 * `RenderableModalBlock` honest: a media `right` cannot appear on a heading in
 * the type the renderers consume.
 *
 * The two branches differ in what that buys today. The text renderers compare
 * `align` to each value they know and emit no class for anything else, so an
 * unlisted value already renders as it would unaligned — the guard protects the
 * type, not the output. The media branches DO change output: an unlisted value
 * becomes `undefined`, which `modalMediaLayoutClass` resolves to the full,
 * centred asset rather than leaving the asset with no width class at all.
 *
 * Both cover a payload the frontend did not write: a hand-edited row, or a
 * choice a later backend adds.
 */
const asOneOf = <TValue extends string>(
  allowed: Readonly<Record<TValue, true>>,
  value: string | undefined,
): TValue | undefined =>
  // `hasOwn` rather than `in`, so an inherited name such as `constructor` cannot
  // pass as a member of the set.
  value !== undefined && Object.hasOwn(allowed, value) ? (value as TValue) : undefined

export const hasModalBlockChildren = (
  block: ModalBlockPayload,
): block is ModalBlockPayload & { children: readonly ModalBlockPayload[] } =>
  Array.isArray(block.children)

const hasVisibleContent = (value: ReactNode): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  if (Array.isArray(value)) {
    return value.some(hasVisibleContent)
  }

  return value !== undefined && value !== null && typeof value !== 'boolean'
}

export const getRenderableModalBlock = (
  block: ModalBlockPayload,
  allowRow: boolean,
  options: ModalBlockRenderOptions = {},
): RenderableModalBlock | undefined => {
  const spacing = asOneOf(MODAL_BLOCK_SPACINGS, block.spacing)

  switch (block.type) {
    case 'heading':
      return hasVisibleContent(block.text)
        ? {
            type: block.type,
            align: asOneOf(MODAL_BLOCK_ALIGNS, block.align),
            highlight: block.highlight,
            size: asOneOf(MODAL_HEADING_SIZES, block.size),
            spacing,
            text: block.text,
          }
        : undefined
    case 'paragraph':
      return hasVisibleContent(block.text)
        ? {
            type: block.type,
            align: asOneOf(MODAL_BLOCK_ALIGNS, block.align),
            highlight: block.highlight,
            spacing,
            style: asOneOf(MODAL_PARAGRAPH_STYLES, block.style),
            text: block.text,
          }
        : undefined
    case 'bullets':
      return Array.isArray(block.items) && block.items.some(hasVisibleContent)
        ? {
            type: block.type,
            align: asOneOf(MODAL_BLOCK_ALIGNS, block.align),
            items: block.items,
            spacing,
          }
        : undefined
    case 'image':
    case 'video':
      return typeof block.url === 'string' && isRenderableMediaUrl(block.url, options)
        ? {
            type: block.type,
            url: block.url,
            align: asOneOf(MODAL_MEDIA_ALIGNS, block.align),
            spacing,
            width: asOneOf(MODAL_MEDIA_WIDTHS, block.width),
          }
        : undefined
    case 'hero':
      // Top level only, like the row: it fills the dialog's width, which no column offers.
      return allowRow && typeof block.url === 'string' && isRenderableMediaUrl(block.url, options)
        ? { type: block.type, url: block.url, spacing }
        : undefined
    case 'cta': {
      const style = asOneOf(MODAL_CTA_STYLES, block.style)

      // An unknown style has no look to draw, so the block cannot render as any CTA.
      if (style === undefined) {
        return undefined
      }

      const link = typeof block.link === 'string' ? block.link : ''
      // A missing destination, not a bad one. Whitespace counts as missing for
      // the same reason the label gate treats it as blank: it is a field the
      // operator has not filled in.
      const isLinkless = link.trim() === '' && options.allowLinklessCta === true

      // A store badge carries its own words, so it needs no label; the block
      // names it after the store instead. An inline link stays text, so it does.
      const drawsStoreBadge = modalCtaStore(style, link) !== null

      return (hasVisibleContent(block.text) || drawsStoreBadge) &&
        (isLinkless || isSafeAnnouncementLink(link))
        ? {
            type: block.type,
            link: isLinkless ? undefined : link,
            spacing,
            style,
            text: block.text,
          }
        : undefined
    }
    case 'card':
      return block.heading !== undefined &&
        block.text !== undefined &&
        (hasVisibleContent(block.heading) || hasVisibleContent(block.text))
        ? {
            type: block.type,
            heading: block.heading,
            spacing,
            style: asOneOf(MODAL_CARD_STYLES, block.style),
            text: block.text,
          }
        : undefined
    case 'empty':
      // An empty block is a deliberate row spacer, never top-level content.
      return allowRow ? undefined : { type: block.type }
    case 'row': {
      if (!allowRow || !hasModalBlockChildren(block)) {
        return undefined
      }

      const children = block.children.flatMap((child) => {
        const renderable = getRenderableModalBlock(child, false, options)

        return renderable === undefined || renderable.type === 'row' || renderable.type === 'hero'
          ? []
          : [renderable]
      })

      return children.some((child) => child.type !== 'empty')
        ? {
            type: block.type,
            children,
            spacing,
            layout: asOneOf(MODAL_ROW_CHOICES.layout, block.layout),

            gap: asOneOf(MODAL_ROW_CHOICES.gap, block.gap),
            vertical_align: asOneOf(MODAL_ROW_CHOICES.vertical_align, block.vertical_align),
            horizontal_align: asOneOf(MODAL_ROW_CHOICES.horizontal_align, block.horizontal_align),
            mobile_layout: asOneOf(MODAL_ROW_CHOICES.mobile_layout, block.mobile_layout),
            mobile_align: asOneOf(MODAL_ROW_CHOICES.mobile_align, block.mobile_align),
          }
        : undefined
    }
    default:
      return undefined
  }
}

/** Whether the renderer can produce any visible block from this payload. */
export const hasRenderableModalBlocks = (blocks: readonly ModalBlockPayload[]): boolean =>
  blocks.some((block) => getRenderableModalBlock(block, true) !== undefined)

/** Blocks the renderer will omit, with enough context for the host to report them. */
export const getDroppedModalBlocks = (
  blocks: readonly ModalBlockPayload[],
): DroppedModalBlock[] => {
  const dropped: DroppedModalBlock[] = []

  const visit = (block: ModalBlockPayload, path: string, allowRow: boolean) => {
    if (block.type === 'row' && allowRow && hasModalBlockChildren(block)) {
      if (block.children.length === 0) {
        dropped.push({ path, reason: 'row has no children', type: block.type })

        return
      }

      block.children.forEach((child, index) => visit(child, `${path}.children[${index}]`, false))

      // Every child can be renderable on its own — a row of nothing but spacers is
      // the case — while the row still renders nothing, so the per-child pass above
      // never witnesses the row itself disappearing.
      if (getRenderableModalBlock(block, true) === undefined) {
        dropped.push({ path, reason: 'row has no visible column', type: block.type })
      }

      return
    }

    if (
      block.type === 'cta' &&
      block.style !== undefined &&
      asOneOf(MODAL_CTA_STYLES, block.style) === undefined
    ) {
      dropped.push({ path, type: block.type, reason: `unsupported CTA style: ${block.style}` })

      return
    }

    if (getRenderableModalBlock(block, allowRow) === undefined) {
      dropped.push({
        path,
        reason:
          block.type === 'row'
            ? 'row children are invalid or nested'
            : block.type === 'hero' && !allowRow
              ? 'a hero cannot sit in a row'
              : 'required fields are invalid or blank',
        type: block.type,
      })
    }
  }

  blocks.forEach((block, index) => visit(block, `blocks[${index}]`, true))

  return dropped
}

const TEXT_BLOCK_TYPES = new Set(['heading', 'paragraph', 'bullets'])
const MEDIA_BLOCK_TYPES = new Set(['image', 'video'])

/**
 * Presentation fields a stored block carried that this build can safely default.
 * Unsupported CTA styles are reported as dropped blocks to avoid promoting an
 * unknown action style to a primary button.
 *
 * `getRenderableModalBlock` degrades such a field to the block's default, which
 * is right for a customer — the modal opens and looks deliberate — but leaves
 * the operator nothing to debug: their authored choice is gone with no signal.
 * The host reports these so the choice this build ignored stays visible.
 *
 * The BUILD path is already covered: a member added to the contract fails to
 * compile against the allow-list Records above. This covers the runtime path no
 * compile gate can reach, where the deployed bundle is older than the backend
 * that serves it.
 */
export const getDowngradedModalFields = (
  blocks: readonly ModalBlockPayload[],
): DowngradedModalField[] => {
  const downgraded: DowngradedModalField[] = []

  const check = <TKey extends string>(
    block: ModalBlockPayload,
    path: string,
    field: DowngradedModalField['field'],
    allowed: Readonly<Record<TKey, true>>,
  ) => {
    const value: string | undefined = block[field]

    // A blank field is an absent one, not a choice this build failed to render.
    if (value !== undefined && value !== '' && !Object.hasOwn(allowed, value)) {
      downgraded.push({ path, field, type: block.type, value })
    }
  }

  const visit = (block: ModalBlockPayload, path: string, topLevel: boolean) => {
    if (hasModalBlockChildren(block)) {
      block.children.forEach((child, index) => visit(child, `${path}.children[${index}]`, false))
    }

    // A column's spacing is never read, so an odd value there is not a lost choice.
    if (topLevel) {
      check(block, path, 'spacing', MODAL_BLOCK_SPACINGS)
    }

    if (block.type === 'heading') {
      check(block, path, 'size', MODAL_HEADING_SIZES)
    }

    if (block.type === 'paragraph') {
      check(block, path, 'style', MODAL_PARAGRAPH_STYLES)
    }

    if (block.type === 'card') {
      check(block, path, 'style', MODAL_CARD_STYLES)
    }

    if (block.type === 'row') {
      check(block, path, 'layout', MODAL_ROW_CHOICES.layout)
      check(block, path, 'gap', MODAL_ROW_CHOICES.gap)
      check(block, path, 'vertical_align', MODAL_ROW_CHOICES.vertical_align)
      check(block, path, 'horizontal_align', MODAL_ROW_CHOICES.horizontal_align)
      check(block, path, 'mobile_layout', MODAL_ROW_CHOICES.mobile_layout)
      check(block, path, 'mobile_align', MODAL_ROW_CHOICES.mobile_align)
    }

    if (TEXT_BLOCK_TYPES.has(block.type)) {
      check(block, path, 'align', MODAL_BLOCK_ALIGNS)
    }

    if (MEDIA_BLOCK_TYPES.has(block.type)) {
      check(block, path, 'align', MODAL_MEDIA_ALIGNS)
      check(block, path, 'width', MODAL_MEDIA_WIDTHS)
    }
  }

  blocks.forEach((block, index) => visit(block, `blocks[${index}]`, true))

  return downgraded
}
